import { cva } from "class-variance-authority";

/**
 * One look for every bordered list: the location access list of the App Store permissions tab. Rounded 12px frame
 * with a gray-300 border, a gray-50 header with 13px text, and 14px rows divided by gray-200 lines.
 */
export const listCardCva = cva("flex flex-col rounded-xl border text-sm", {
  variants: {
    /** A state, not a look: the selection in the list has an error. */
    invalid: {
      true: "border-danger-500 dark:border-danger-400",
      false: "border-gray-300 dark:border-dark-600",
    },
  },
  defaultVariants: { invalid: false },
});

/** Header and footer bar. `first` and `last` round the corners that touch the frame. */
export const listCardBarCva = cva(
  "flex items-center justify-between gap-3 bg-gray-50 px-3.5 py-2.5 text-[13px] text-gray-700 dark:bg-dark-700 dark:text-gray-400",
  {
    variants: {
      first: { true: "rounded-t-xl", false: "border-t border-gray-200 dark:border-dark-600" },
      last: { true: "rounded-b-xl", false: null },
    },
    defaultVariants: { first: true, last: false },
  },
);

/** The column header of a grid list; it shares the grid template of the rows. */
export const listCardColumnsCva = cva(
  "grid items-center gap-x-4 bg-gray-50 px-3.5 py-2.5 text-xs font-medium text-gray-700 dark:bg-dark-700 dark:text-gray-400",
  {
    variants: {
      first: { true: "rounded-t-xl", false: "border-t border-gray-200 dark:border-dark-600" },
    },
    defaultVariants: { first: true },
  },
);

/** The list body; rows get a divider between them, and the first row one towards the header above it. */
export const listCardBodyCva = cva("m-0 flex list-none flex-col divide-y divide-gray-200 p-0 dark:divide-dark-600", {
  variants: {
    first: { true: "rounded-t-xl", false: "border-t border-gray-200 dark:border-dark-600" },
    last: { true: "rounded-b-xl", false: null },
    scroll: { true: "overflow-y-auto overscroll-contain", false: null },
  },
  defaultVariants: { first: true, last: true, scroll: false },
});

export const listCardRowCva = cva(
  "relative px-3.5 first:rounded-t-[inherit] last:rounded-b-[inherit]",
  {
    variants: {
      subheader: {
        true: "bg-gray-50 py-2 text-xs font-medium text-gray-700 dark:bg-dark-700 dark:text-gray-400",
        false: null,
      },
      /** One compact line instead of the two-line row. */
      dense: { true: null, false: null },
      selected: { true: "bg-primary-50/60 dark:bg-primary-900/20", false: null },
      interactive: { true: "transition-colors hover:bg-gray-50 dark:hover:bg-dark-700/60", false: null },
      disabled: { true: "cursor-not-allowed", false: null },
    },
    compoundVariants: [
      { subheader: false, dense: false, class: "py-3" },
      { subheader: false, dense: true, class: "py-1.5" },
      { interactive: true, disabled: true, class: "hover:bg-transparent dark:hover:bg-transparent" },
    ],
    defaultVariants: { subheader: false, dense: false, selected: false, interactive: false, disabled: false },
  },
);

export const listCardRowTitleCva = cva("block min-w-0 text-left text-sm", {
  variants: {
    tone: {
      default: "text-(--text)",
      muted: "text-gray-500 dark:text-gray-500",
      danger: "text-danger-600 dark:text-danger-400",
      active: "font-medium text-primary-700 dark:text-primary-400",
    },
    disabled: { true: null, false: null },
    /** A stretched label, button or link: its `::after` covers the hit area, so the whole row is clickable. */
    stretched: {
      true: "outline-hidden after:absolute focus-visible:after:ring-2 focus-visible:after:ring-primary-500 focus-visible:after:ring-inset",
      false: null,
    },
    /**
     * What the stretched `::after` covers. `row`: the whole row. `line`: only the line with the title and the row's
     * padding above and beside it (the line is the positioning context), so an open `expanded` part under it and the
     * gap above that part are not part of the hit area.
     */
    hitArea: {
      row: null,
      line: null,
    },
    /** Lets the text wrap onto more lines instead of cutting it off with an ellipsis. */
    wrap: { true: "whitespace-normal wrap-break-word", false: "truncate" },
  },
  compoundVariants: [
    { stretched: true, disabled: false, class: "cursor-pointer" },
    { stretched: true, disabled: true, class: "cursor-not-allowed" },
    { stretched: true, hitArea: "row", class: "after:inset-0" },
    { stretched: true, hitArea: "line", class: "after:-inset-x-3.5 after:-top-3 after:bottom-0" },
  ],
  defaultVariants: { tone: "default", disabled: false, stretched: false, hitArea: "row", wrap: false },
});

export const listCardRowSubtitleCva = cva("block min-w-0 text-xs text-gray-700 dark:text-gray-400", {
  variants: {
    wrap: { true: "whitespace-normal wrap-break-word", false: "truncate" },
    /** On the title line: it shrinks before the title does. */
    dense: { true: "flex-1", false: null },
  },
  defaultVariants: { wrap: false, dense: false },
});
